import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Check, Minus, Star, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useErrorToast } from "@/lib/hooks";
import { useWs } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";
import { cn } from "@/lib/utils";

const PriceBlock = ({ p, yearly, base, yearPrice, fp }) => {
  const { t } = useI18n();
  if (p.quote) return <span className="text-3xl font-extrabold">{t("plan.quote")}</span>;
  if (!yearly || base <= 0) {
    return <><span className="text-4xl font-extrabold" data-testid={`plan-price-${p.id}`}>{fp(base)}</span><span className="text-slate-500 text-sm">{t("plan.perMonth")}</span></>;
  }
  return (
    <span className="block" data-testid={`plan-yearly-price-${p.id}`}>
      <span className="text-4xl font-extrabold">{fp(yearPrice)}</span><span className="text-slate-500 text-sm">{t("plan.perYear")}</span>
      <span className="mt-1 flex flex-wrap items-center gap-2 text-sm font-body">
        <span className="text-slate-400 line-through">{fp(base * 12)}</span>
        {p.yearly_discount > 0 && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700">{t("plan.save", { pct: p.yearly_discount })}</span>}
        <span className="text-slate-500">{t("plan.yearlyEquiv", { price: fp(yearPrice / 12) })}</span>
      </span>
    </span>
  );
};

const PlanCard = ({ p, current, stars, yearly, cur }) => {
  const { t, fmtNum, fmtPrice } = useI18n();
  const showErr = useErrorToast();
  const { canManage } = useWs();
  const [busy, setBusy] = useState(false);
  const L = p.limits;
  const base = cur === "EUR" ? (p.price_eur ?? p.price_chf) : p.price_chf;
  const yearPrice = Math.round(base * 12 * (100 - (p.yearly_discount || 0))) / 100;
  const fp = (v) => fmtPrice(v, cur);
  const n = (v, k) => (v < 0 ? `${t("common.unlimited")} ${t(k)}` : `${fmtNum(v)} ${t(k)}`);
  const choose = async () => {
    setBusy(true);
    try {
      if (p.quote) { await api.post("/plans/interest", { plan_id: p.id }); toast.success(t("plan.interestSent")); setBusy(false); return; }
      toast.info(t("pay.redirecting"));
      const { data } = await api.post("/payments/checkout", { plan_id: p.id, origin_url: window.location.origin, interval: yearly ? "year" : "month", currency: cur.toLowerCase() });
      window.location.href = data.checkout_url;
    } catch (e) { showErr(e); setBusy(false); }
  };
  const feats = [
    [true, n(L.items, "plan.items"), "items"], [true, n(L.places, "plan.places"), "places"], [true, n(L.members, "plan.members"), "members"],
    [p.features.ai, p.features.ai ? n(L.ai, "plan.aiPerMonth") : `0 ${t("plan.aiPerMonth")}`, "ai"], [p.features.qr, t("plan.qr"), "qr"],
    [p.features.history, t("plan.history"), "history"], [true, t("plan.export"), "export"],
  ];
  return (
    <div data-testid={`plan-card-${p.id}`} className={cn("flex flex-col rounded-2xl border bg-white p-6 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg",
      current ? "border-blue-600 ring-2 ring-blue-600" : "border-slate-200")}>
      <div className="flex items-center gap-1 h-5 text-amber-500">{Array.from({ length: stars }, (_, n) => `star-${n}`).map((id) => <Star key={id} className="w-4 h-4 fill-current" />)}</div>
      <h3 className="mt-3 text-xl font-extrabold">{t(`planName.${p.id}`)}</h3>
      <p className="mt-3 mb-6 font-head"><PriceBlock p={p} yearly={yearly} base={base} yearPrice={yearPrice} fp={fp} /></p>
      <ul className="space-y-2.5 text-sm flex-1">
        {feats.map(([ok, label, id]) => (
          <li key={id} className={cn("flex items-center gap-2", !ok && "text-slate-400")}>
            {ok ? <Check className="w-4 h-4 text-emerald-500 shrink-0" /> : <Minus className="w-4 h-4 shrink-0" />}{label}
          </li>
        ))}
      </ul>
      {current ? <p data-testid={`plan-current-${p.id}`} className="mt-6 rounded-lg bg-blue-50 py-3 text-center text-sm font-semibold text-blue-700">{t("plan.current")}</p>
        : p.id !== "free" && (canManage || p.quote) && <Button data-testid={`plan-choose-${p.id}`} disabled={busy} onClick={choose} className="mt-6 h-12">{p.quote ? t("plan.contact") : t("plan.choose")}</Button>}
    </div>
  );
};

const defaultCurrency = () => {
  const saved = localStorage.getItem("rangeo_currency");
  if (saved === "CHF" || saved === "EUR") return saved;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const swiss = tz === "Europe/Zurich" || (navigator.languages || []).some((l) => /-CH$/i.test(l));
  return swiss ? "CHF" : "EUR";
};

export default function Plans() {
  const { t, fmtDate } = useI18n();
  const { ws, plan, status, canManage } = useWs();
  const showErr = useErrorToast();
  const openPortal = async () => {
    try { const { data } = await api.post("/payments/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { showErr(e); }
  };
  const { data: plans = [] } = useQuery({ queryKey: ["plans"], queryFn: () => api.get("/plans").then((r) => r.data) });
  const groups = [["personal", "plan.personalGroup", Star], ["business", "plan.businessGroup", Building2]];
  let starIdx = 0;
  const [yearly, setYearly] = useState(false);
  const [cur, setCurState] = useState(defaultCurrency);
  const setCur = (c) => { localStorage.setItem("rangeo_currency", c); setCurState(c); };
  const maxDiscount = Math.max(0, ...plans.map((p) => p.yearly_discount || 0));

  return (
    <div className="space-y-12">
      <PageHeader title={t("plan.title")} subtitle={t("plan.subtitle")} testId="plans-title" />
      <div className="rounded-2xl bg-slate-900 p-5 sm:p-6 text-white flex flex-wrap items-center justify-between gap-3" data-testid="plan-status">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400">{t("plan.current")}</p>
          <p className="text-xl font-extrabold font-head">{t(`planName.${plan.id}`)}</p>
        </div>
        {canManage && ws.has_subscription && status === "paid" && (
          <Button data-testid="manage-subscription-button" variant="secondary" className="h-11 gap-2" onClick={openPortal}><CreditCard className="w-4 h-4" />{t("pay.manage")}</Button>
        )}
        <p className="text-sm text-slate-300">
          {status === "trial" ? t("plan.trialEnds", { date: fmtDate(ws.trial_ends_at) }) : status === "paid" && ws.plan_expires_at ? t("plan.expires", { date: fmtDate(ws.plan_expires_at) }) : t(`status.${status}`)}
        </p>
      </div>
      <p className="text-sm text-slate-500 -mt-6" data-testid="pay-methods">{t("pay.methods")}{!canManage && ` · ${t("pay.ownerOnly")}`}</p>
      <div className="flex flex-wrap items-center gap-3">
      <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="currency-toggle">
        {["CHF", "EUR"].map((c) => (
          <button key={c} data-testid={`currency-${c.toLowerCase()}`} onClick={() => setCur(c)}
            className={cn("rounded-full px-4 py-2 font-mono text-sm font-semibold transition-colors", cur === c ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100")}>{c}</button>
        ))}
      </div>
      <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="billing-interval-toggle">
        {[[false, "plan.monthly"], [true, "plan.yearly"]].map(([v, k]) => (
          <button key={k} data-testid={`interval-${v ? "yearly" : "monthly"}`} onClick={() => setYearly(v)}
            className={cn("flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors", yearly === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100")}>
            {t(k)}{v && maxDiscount > 0 && <span className="rounded-full bg-emerald-400 px-1.5 text-[10px] font-bold text-slate-900">{t("plan.save", { pct: maxDiscount })}</span>}
          </button>
        ))}
      </div>
      </div>
      {groups.map(([g, label, Icon]) => (
        <section key={g} className="space-y-5">
          <h2 className="flex items-center gap-2 text-2xl font-extrabold"><Icon className="w-5 h-5 text-blue-600" />{t(label)}</h2>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {plans.filter((p) => p.group === g).map((p) => {
              const stars = g === "personal" && p.id !== "free" ? ++starIdx : 0;
              return <PlanCard key={p.id} p={p} stars={stars} yearly={yearly} cur={cur} current={plan.id === p.id || (p.id === "free" && status === "free")} />;
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
